import { useCallback, useEffect, useMemo, useState } from 'react';
import { vaultWrite } from '../lib/vaultWrite';
import { vaultList } from '../lib/vaultRead';
import { usePolling } from '../lib/poll';
import { IconFilter, IconHistory, IconLock, IconUsers } from '../ui/icons';
import { Toggle } from '../ui/toggle';
import { groupByCategory } from './categories';
import VaultCredentialModal from './VaultCredentialModal';
import VaultAccessModal from './VaultAccessModal';
import VaultActivityLogModal from './VaultActivityLogModal';
import VaultCategoryFilterModal from './VaultCategoryFilterModal';

const cardStyle = { background: '#fff', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-xl)', boxShadow: 'var(--shadow-card)' };

// Column-header cell. Extracted because the list header repeats it per column
// and the string is long enough that four inline copies obscure the layout.
const colHead = {
  fontSize: 10, fontWeight: 600, color: 'var(--text-subdued)',
  textTransform: 'uppercase', letterSpacing: 'var(--tracking-caps)',
};

// Width reserved for the admin-only Archive button, applied to BOTH the header
// spacer and the row's button cell so the two stay in step. Without this the
// header columns sit wider than the row columns for admins.
const ACTION_COL = '0 0 68px';

// Notes are a free-text textarea, so they can be long and multi-line. Rows show
// a one-line preview (newlines collapsed, or a tall row would break the table)
// with the full text on hover; the drawer already shows it in full.
function notePreview(notes) {
  if (!notes) return '';
  return String(notes).replace(/\s+/g, ' ').trim();
}

function EmptyState({ children }) {
  return (
    <div style={{ textAlign: 'center', padding: '48px 20px', color: 'var(--text-subdued)', fontSize: 13 }}>
      <div style={{ width: 36, height: 36, borderRadius: 999, background: 'var(--surface-default)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px', color: 'var(--text-disabled)' }}>
        <IconLock size={16} />
      </div>
      {children}
    </div>
  );
}

// Same pill shape/size for all three action buttons, but each gets its own
// accent colour so they read as distinct actions, not one repeated button —
// Filter is a neutral utility, Access Log is informational, Manage Access
// is the admin/management action (kept on the site's actual primary purple).
function AccentButton({ onClick, children, color = '#6b7280' }) {
  const soft = `${color}14`;
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 6,
        padding: '7px 14px', fontSize: 12, fontWeight: 600, borderRadius: 8,
        border: `1px solid ${color}`, cursor: 'pointer', fontFamily: 'inherit',
        background: soft, color,
        boxShadow: 'var(--shadow-xs)',
        transition: 'background 150ms var(--ease-default), color 150ms var(--ease-default), transform 150ms var(--ease-default), box-shadow 150ms var(--ease-default)',
      }}
      onMouseEnter={e => { e.currentTarget.style.background = color; e.currentTarget.style.color = '#fff'; e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = `0 4px 12px ${color}4D`; }}
      onMouseLeave={e => { e.currentTarget.style.background = soft; e.currentTarget.style.color = color; e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'var(--shadow-xs)'; }}
    >
      {children}
    </button>
  );
}

// Password Vault — a shared credential store (site/login/email/password per
// company tool) doubling as an access log, per Mo's 2026-07-16 request.
// Admins manage (create/edit/archive/grant access); everyone else with page
// access only consumes — views what's shared with them, reveals/copies if
// their grant allows, and reads the Access Log. The list groups by category
// (taxonomy order) so it's browsable at a glance instead of one flat table.
export default function VaultPage({ session }) {
  const isAdmin = session.role === 'admin';
  const [credentials, setCredentials] = useState([]);
  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState(null); // null = no filter
  const [collapsed, setCollapsed] = useState(() => new Set());
  const [drawer, setDrawer] = useState(null);
  const [accessOpen, setAccessOpen] = useState(false);
  const [logOpen, setLogOpen] = useState(false);
  const [filterOpen, setFilterOpen] = useState(false);
  const [separated, setSeparated] = useState(false); // false = one list, true = own card per category

  // vault-list filters server-side by who's asking: admins get everything,
  // everyone else only gets credentials they hold an active grant on. Each
  // row comes back with my_level ('admin' | 'viewer' | 'revealer'), which
  // the drawer uses to decide whether Reveal shows at all.
  const loadCredentials = useCallback(async () => {
    const { data } = await vaultList(session);
    setCredentials((data || []).filter(c => !c.archived_at).sort((a, b) => (a.site_name || '').localeCompare(b.site_name || '')));
  }, [session]);

  useEffect(() => { loadCredentials(); }, [loadCredentials]);
  usePolling(loadCredentials);

  const categoryCounts = useMemo(() => {
    const c = {};
    credentials.forEach(cr => { c[cr.category] = (c[cr.category] || 0) + 1; });
    return c;
  }, [credentials]);

  const displayed = useMemo(() => {
    const q = search.trim().toLowerCase();
    return credentials.filter(c => {
      if (activeCategory && c.category !== activeCategory) return false;
      if (q) {
        // Notes are searchable too — a note column you can't search is only
        // half a feature, and notes often hold the thing you're hunting for
        // (recovery email, account number, who owns the login).
        const hit = (c.site_name || '').toLowerCase().includes(q) || (c.login_username || '').toLowerCase().includes(q) || (c.login_email || '').toLowerCase().includes(q) || (c.notes || '').toLowerCase().includes(q);
        if (!hit) return false;
      }
      return true;
    });
  }, [credentials, search, activeCategory]);

  const groups = useMemo(() => groupByCategory(displayed), [displayed]);
  const allCollapsed = groups.length > 0 && groups.every(g => collapsed.has(g.key));

  function toggleGroup(key) {
    setCollapsed(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  }

  function toggleAll() {
    setCollapsed(allCollapsed ? new Set() : new Set(groups.map(g => g.key)));
  }

  async function archiveCredential(c) {
    if (!confirm(`Archive ${c.site_name}? It will drop off the list but stay recoverable.`)) return;
    await vaultWrite(session, 'vault_credentials', 'update', { archived_at: new Date().toISOString(), archived_by: session.name }, { column: 'id', value: c.id });
    loadCredentials();
  }

  // Shared between the two list layouts (one shared table vs. one card per
  // category) so the row/header markup only lives in one place.
  function renderColumnHeader() {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '8px 16px 8px 34px', borderBottom: '1px solid var(--border-subdued)' }}>
        <div style={{ flex: '0 0 220px', ...colHead }}>Site</div>
        <div style={{ flex: '0 0 200px', ...colHead }}>Login / Email</div>
        {/* Link gets the larger share — URLs are long, notes are usually short.
            minWidth:0 is required for ellipsis to work inside a flex child. */}
        <div style={{ flex: '1.5 1 0', minWidth: 0, ...colHead }}>Login Link</div>
        <div style={{ flex: '1 1 0', minWidth: 0, ...colHead }}>Note</div>
        {isAdmin && <div style={{ flex: ACTION_COL }} aria-hidden="true" />}
      </div>
    );
  }

  function renderGroupHeader(group) {
    const isCollapsed = collapsed.has(group.key);
    return (
      <div onClick={() => toggleGroup(group.key)}
        style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 16px', background: 'var(--surface-elevated)', cursor: 'pointer', userSelect: 'none' }}>
        <span style={{ fontSize: 9, color: 'var(--text-subdued)', transform: isCollapsed ? 'rotate(-90deg)' : 'none', transition: 'transform 120ms var(--ease-default)' }}>▾</span>
        <span style={{ width: 8, height: 8, borderRadius: 999, background: group.color, flexShrink: 0 }} />
        <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-emphasized)' }}>{group.label}</span>
        <span style={{ fontSize: 11, color: 'var(--text-subdued)' }}>{group.items.length}</span>
      </div>
    );
  }

  function renderRow(c) {
    return (
      <div key={c.id} onClick={() => setDrawer({ credential: c })}
        style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '10px 16px 10px 34px', borderTop: '1px solid var(--border-subdued)', cursor: 'pointer', transition: 'background 120ms var(--ease-default)' }}
        onMouseEnter={e => e.currentTarget.style.background = 'var(--surface-default)'} onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
        <div style={{ flex: '0 0 220px', fontWeight: 500, color: 'var(--text-emphasized)', fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.site_name}</div>
        <div style={{ flex: '0 0 200px', color: 'var(--text-subdued)', fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.login_email || c.login_username || '--'}</div>
        <div style={{ flex: '1.5 1 0', minWidth: 0, fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {c.url ? (
            <a href={c.url} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()}
              style={{ color: 'var(--color-primary)', textDecoration: 'none' }}
              onMouseEnter={e => e.currentTarget.style.textDecoration = 'underline'} onMouseLeave={e => e.currentTarget.style.textDecoration = 'none'}>
              {c.url}
            </a>
          ) : <span style={{ color: 'var(--text-subdued)' }}>--</span>}
        </div>
        {/* title carries the untruncated note so the full text is one hover away;
            the row click already opens the drawer, which shows it in full. */}
        <div title={notePreview(c.notes) || undefined}
          style={{ flex: '1 1 0', minWidth: 0, fontSize: 12, color: 'var(--text-subdued)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {notePreview(c.notes) || <span style={{ color: 'var(--text-subdued)' }}>--</span>}
        </div>
        {isAdmin && (
          <div style={{ flex: ACTION_COL, display: 'flex', justifyContent: 'flex-end' }}>
            <button type="button" className="btn btn-secondary btn-sm" style={{ fontSize: 9, flexShrink: 0 }} onClick={e => { e.stopPropagation(); archiveCredential(c); }}>Archive</button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="app-screen">
      <div className="main-content">
        {/* Page header: title, and three modal triggers — Filter, Access
            Log (anyone with page access), Manage Access (admin-only). */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
          <div>
            <h1 style={{ fontSize: 22, fontWeight: 600, color: 'var(--text-emphasized)', margin: '0 0 4px', letterSpacing: 'var(--tracking-tight)' }}>Vault</h1>
            <p style={{ fontSize: 13, color: 'var(--text-subdued)', margin: 0 }}>Every company login in one place — and who's looked at what</p>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <AccentButton color="#6b7280" onClick={() => setFilterOpen(true)}>
              <IconFilter size={12} /> {activeCategory || 'Filter'}
              {activeCategory && <span onClick={e => { e.stopPropagation(); setActiveCategory(null); }} style={{ marginLeft: 2, opacity: 0.8 }}>×</span>}
            </AccentButton>
            <AccentButton color="#2563eb" onClick={() => setLogOpen(true)}><IconHistory size={12} /> Access Log</AccentButton>
            {isAdmin && <AccentButton color="#7c3aed" onClick={() => setAccessOpen(true)}><IconUsers size={12} /> Manage Access</AccentButton>}
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, gap: 12, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <button type="button" className="btn btn-secondary btn-sm" onClick={toggleAll} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: 9, transform: allCollapsed ? 'rotate(-90deg)' : 'none', transition: 'transform 120ms var(--ease-default)' }}>▾</span>
              {allCollapsed ? 'Expand all' : 'Collapse all'}
            </button>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: 'var(--text-default)', cursor: 'pointer' }}>
              <Toggle checked={separated} onChange={v => { setSeparated(v); setCollapsed(new Set()); }} />
              Separate table per category
            </label>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <input type="text" placeholder="Search credentials..." value={search} onChange={e => setSearch(e.target.value)}
              style={{ padding: '7px 12px', fontSize: 12, border: '1px solid var(--border-default)', borderRadius: 8, width: 200, fontFamily: 'inherit', color: 'var(--text-emphasized)', background: 'var(--surface-input)', transition: 'box-shadow 150ms var(--ease-default), border-color 150ms var(--ease-default)' }}
              onFocus={e => { e.currentTarget.style.boxShadow = 'var(--shadow-focus)'; e.currentTarget.style.borderColor = 'var(--color-primary)'; }}
              onBlur={e => { e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.borderColor = 'var(--border-default)'; }} />
            {isAdmin && <button className="btn btn-primary btn-sm" onClick={() => setDrawer({ credential: null })} style={{ whiteSpace: 'nowrap' }}>+ New Credential</button>}
          </div>
        </div>

        {displayed.length === 0 ? (
          <div style={cardStyle}>
            <EmptyState>
              {credentials.length === 0 && session.role !== 'admin'
                ? "No credentials shared with you yet — ask an admin for access."
                : 'No credentials found'}
            </EmptyState>
          </div>
        ) : separated ? (
          // Own card per category — each a fully separate table, not one
          // shared container split by header rows.
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {groups.map(group => (
              <div key={group.key} style={{ ...cardStyle, overflow: 'hidden' }}>
                {renderGroupHeader(group)}
                {!collapsed.has(group.key) && (<>
                  {renderColumnHeader()}
                  {group.items.map(c => renderRow(c))}
                </>)}
              </div>
            ))}
          </div>
        ) : (
          <div style={{ ...cardStyle, overflow: 'hidden' }}>
            {renderColumnHeader()}
            {groups.map((group, i) => (
              <div key={group.key} style={{ borderTop: i > 0 ? '1px solid var(--border-subdued)' : 'none' }}>
                {renderGroupHeader(group)}
                {!collapsed.has(group.key) && group.items.map(c => renderRow(c))}
              </div>
            ))}
          </div>
        )}
      </div>

      {drawer && <VaultCredentialModal session={session} credential={drawer.credential} onClose={() => setDrawer(null)} onSaved={loadCredentials} />}
      {accessOpen && isAdmin && <VaultAccessModal session={session} credentials={credentials} onClose={() => setAccessOpen(false)} />}
      {logOpen && <VaultActivityLogModal session={session} credentials={credentials} onClose={() => setLogOpen(false)} onRestored={loadCredentials} />}
      {filterOpen && (
        <VaultCategoryFilterModal
          categoryCounts={categoryCounts}
          activeCategory={activeCategory}
          onSelect={setActiveCategory}
          onClose={() => setFilterOpen(false)}
        />
      )}
    </div>
  );
}
